















































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { auth } from "@/config/host.config";
import { IRoom } from "@/@types/hotel/room";
import { IVirtualRooma } from "@/@types/hotel/virtual_rooms";
import { ILocale } from "@/@types/locale";
import InputSearch from "@/components/app/search/input-search.vue";

interface IRoomLevelItem {
  _id: string;
  name: ILocale;
}
interface IRoomLevelMap {
  [levelId: string]: Array<Array<string>>;
}

@Component({
  components: { "input-search": InputSearch },
})
class RoomPicker extends Vue {
  @Prop({ type: Boolean, default: false }) show!: boolean;

  loading = false;
  search = "";
  cellSize = 48;
  waif: Array<IRoom> = [];

  rooms: Array<IRoom> = [];
  levels: Array<IRoomLevelItem> = [];
  virtualRooms: Array<IVirtualRooma> = [];

  @Watch("show")
  onShow(val: boolean): void {
    if (val) {
      this.search = "";
      this.waif = [];
      this.load();
    }
  }

  get cellWidthPx(): string {
    return `${this.cellSize}px`;
  }

  get filteredRooms(): Array<IRoom> {
    if (!this.search.trim()) return this.rooms;
    const q = this.search.trim().toLowerCase();
    return this.rooms.filter((r) => (r.name || "").toLowerCase().includes(q));
  }

  get roomList(): IRoomLevelMap {
    const levels: IRoomLevelMap = {};
    this.waif = [];
    const levelsId = this.levels.map((l) => l._id);
    const rooms = this.filteredRooms;

    const maximus: { [level: string]: { x: number; y: number } } = {};
    let maxx = 0;
    rooms.forEach((el) => {
      if (
        el.level &&
        el.position &&
        el.position.x != null &&
        el.position.x !== undefined &&
        el.position.y != null &&
        el.position.y !== undefined &&
        levelsId.includes(el.level)
      ) {
        const x = parseInt(el.position.x) || 0;
        const y = parseInt(el.position.y) || 0;
        const prev = maximus[el.level] || { x: 0, y: 0 };
        maximus[el.level] = {
          x: Math.max(prev.x, x),
          y: Math.max(prev.y, y),
        };
        maxx = Math.max(maxx, x);
      }
    });

    if (!Object.keys(maximus).length) {
      rooms.forEach((el) => {
        if (!this.hasPosition(el)) this.waif.push(el);
      });
      return levels;
    }

    this.levels.forEach((lv) => {
      if (!maximus[lv._id]) return;
      const yLen = maximus[lv._id].y + 1;
      const xLen = maximus[lv._id].x + 1;
      const grid: Array<Array<string>> = [];
      for (let i = 0; i < yLen; i++) {
        grid.push(new Array(xLen).fill("*"));
      }
      levels[lv._id] = grid;
    });

    rooms.forEach((el) => {
      if (
        !el.level ||
        !el.position ||
        el.position.x == null ||
        el.position.x === undefined ||
        el.position.y == null ||
        el.position.y === undefined ||
        !levelsId.includes(el.level) ||
        !maximus[el.level]
      ) {
        this.waif.push(el);
        return;
      }
      const x = parseInt(el.position.x) || 0;
      const y = parseInt(el.position.y) || 0;
      if (levels[el.level] && levels[el.level][y]) {
        levels[el.level][y][x] = el._id;
      }
    });

    return levels;
  }

  hasPosition(el: IRoom): boolean {
    return Boolean(
      el.level &&
        el.position &&
        el.position.x != null &&
        el.position.x !== undefined &&
        el.position.y != null &&
        el.position.y !== undefined &&
        this.levels.some((l) => l._id === el.level)
    );
  }

  getRoom(id: string): IRoom | undefined {
    if (id === "*") return undefined;
    return this.rooms.find((r) => r._id === id);
  }

  roomName(id: string): string {
    const r = this.getRoom(id);
    return r && r.name ? r.name : "";
  }

  vroomFor(roomId: string): IVirtualRooma | undefined {
    return this.virtualRooms.find((vr) => vr.room === roomId);
  }

  isOccupied(roomId: string): boolean {
    const vr = this.vroomFor(roomId);
    return Boolean(vr && vr.guests && vr.guests.length > 0);
  }

  hasVroom(roomId: string): boolean {
    return Boolean(this.vroomFor(roomId));
  }

  getLevelName(levelId: string): string {
    const lv = this.levels.find((l) => l._id === levelId);
    if (!lv || !lv.name)
      return String(this.$t("roomService.roomPicker.defaultLevel"));
    return lv.name[this.$i18n.locale] || lv.name.ru || lv.name.en || levelId;
  }

  cellClass(id: string): Record<string, boolean> {
    if (id === "*") return { "rs-cell--empty": true };
    const hasVroom = this.hasVroom(id);
    const occupied = this.isOccupied(id);
    return {
      "rs-cell--off": !hasVroom,
      "rs-cell--ok": hasVroom && !occupied,
      "rs-cell--busy": hasVroom && occupied,
    };
  }

  cellHint(id: string): string {
    if (id === "*") return "";
    if (!this.hasVroom(id))
      return String(this.$t("roomService.roomPicker.noVroom"));
    return this.isOccupied(id)
      ? String(this.$t("roomService.roomPicker.occupied"))
      : String(this.$t("roomService.roomPicker.available"));
  }

  cellTitle(id: string): string {
    if (id === "*") return "";
    const name = this.roomName(id);
    return `${name} — ${this.cellHint(id)}`;
  }

  onPick(id: string): void {
    if (id === "*") return;
    const room = this.getRoom(id);
    if (!room) return;
    const vroom = this.vroomFor(id);
    this.$emit("pick", { room, vroom });
  }

  close(): void {
    this.$emit("close");
  }

  async load(): Promise<void> {
    this.loading = true;
    const params = {
      "match[show]": true,
      "match[deleted][value]": false,
    };
    try {
      const [roomsRes, levelsRes, vroomsRes] = await Promise.all([
        this.$axios.get(auth.room, { params }),
        this.$axios.get(auth.room_level, { params }),
        this.$axios.get(auth.virtual_rooms, { params }),
      ]);
      if (roomsRes.status === 200) this.rooms = roomsRes.data;
      if (levelsRes.status === 200) this.levels = levelsRes.data;
      if (vroomsRes.status === 200) this.virtualRooms = vroomsRes.data;
    } catch (e) {
      console.log(e);
    } finally {
      this.loading = false;
    }
  }
}
export default RoomPicker;
